import { type Field } from '@lighthouse/core'
import { useVirtualizer } from '@tanstack/react-virtual'
import { max } from 'rambda'
import React, { startTransition, useEffect, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { useApplicationContext } from '../../contexts'
import { desktopRowHeight, mobileRowHeight } from './constant'
import { useFieldSelectTableContext } from './FiledSelectTableContext'
import { TableRecordMobile } from './mobile/TableRecordMobile'
import { TableIndex } from './TableIndex'

interface TableContentProps {
    columns: Field[]
    tableWidth?: number
    tableHeight?: number
    scrollXRef: React.RefObject<HTMLDivElement>
    scrollYRef: React.RefObject<HTMLDivElement>
}

const SCTableBody = styled.div`
    width: 100%;
`

const SCxTableContainer = styled.div`
    position: relative;
    min-width: 100%;
    width: 100%;
`

const SCxTableRow = styled.div`
    width: 100%;
    height: 39px;
    border-bottom: 1px solid var(--color-gray-200);
`

export const TableBody: React.FC<TableContentProps> = ({ columns, tableWidth, tableHeight, scrollXRef, scrollYRef }) => {
    const [scrollDom, setScrollDom] = useState<HTMLDivElement | null>(scrollYRef.current)
    const { dsId, recordPoints, value, loading } = useFieldSelectTableContext()
    const { isMobile } = useApplicationContext()

    const estimateSize = useMemo(() => {
        if (!scrollDom || !isMobile) {
            return 160
        }
        const { width } = scrollDom.getBoundingClientRect()
        const renderColumnsNum = columns.length - 1

        return width > renderColumnsNum * 160 ? width / renderColumnsNum : 160
    }, [columns.length, isMobile, scrollDom])

    const currentRowHeight = mobileRowHeight

    const rowOverscan = useMemo(() => {
        if (!tableHeight) {
            return 300
        }
        return max(Math.floor(tableHeight / currentRowHeight) + 10, 300)
    }, [currentRowHeight, tableHeight])

    const rowVirtualizer = useVirtualizer({
        count: recordPoints?.length ?? 0,
        scrollPaddingStart: currentRowHeight,
        getScrollElement: () => scrollXRef.current,
        estimateSize: () => currentRowHeight,
        getItemKey: (index: number) => recordPoints[index].id,
        overscan: rowOverscan
    })

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: columns.length - 1,
        getScrollElement: () => scrollYRef.current,
        paddingStart: 0,
        paddingEnd: 40,
        estimateSize: () => estimateSize,
        overscan: 8
    })

    useEffect(() => {
        if (!scrollDom) {
            setScrollDom(scrollXRef.current)
        }
    }, [scrollDom, scrollXRef])

    useUpdateEffect(() => {
        if (scrollDom) {
            startTransition(() => {
                columnVirtualizer.measure()
                rowVirtualizer.measure()
            })
        }
    }, [scrollDom])

    return (
        <SCTableBody>
            <SCxTableContainer
                style={{
                    width: `${columnVirtualizer.getTotalSize()}px`,
                    height: `${rowVirtualizer.getTotalSize()}px`
                }}
            >
                {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const { index } = virtualRow
                    const { id: recordId, disabled = false } = recordPoints[index]
                    const recordProp = { recordId, value, index, tableWidth, columns, virtualRow, columnVirtualizer, disabled }
                    return (
                        <TableRecordMobile
                            {...recordProp}
                            dsId={dsId}
                            key={recordId}
                            stickyLeft={isMobile ? 0 : 32}
                            isMobile={isMobile}
                            position={isMobile ? 'right' : 'left'}
                        />
                    )
                })}
            </SCxTableContainer>
            {loading && (
                <SCxTableRow>
                    <TableIndex loading />
                </SCxTableRow>
            )}
        </SCTableBody>
    )
}
